import * as Api from '@ViewModels';

// The API DTO serializes `property` as the enum ordinal while the persisted domain form uses the
// string name — accept both so parsing works against either payload shape.
export const isTagCriterion = (criterion: Api.IContactFilterCriteria) =>
	criterion.property === Api.ContactFilterCriteriaProperty.Tag || (criterion.property as unknown) === 'Tag';

/**
 * Flattens any top-level criterion that is a pure Or-tag group (no own property/value, op Or, all
 * children are tag criteria with a value) into its children. This normalizes the nested shape that
 * AdvancedTagFilterCollection.toCriteriaCollection emits for multi-tag selections so that the rest
 * of the audience logic can assume a single canonical flat shape for tag criteria.
 */
export function normalizeAudienceCriteria(criteria: Api.IContactFilterCriteria[]): Api.IContactFilterCriteria[] {
	const result: Api.IContactFilterCriteria[] = [];
	for (const criterion of criteria) {
		const isPureOrTagGroup =
			criterion.property == null &&
			criterion.value == null &&
			(criterion.op === Api.FilterOperator.Or || criterion.op == null) &&
			!!criterion.criteria?.length &&
			criterion.criteria.every(child => isTagCriterion(child) && child.value != null);
		if (isPureOrTagGroup) {
			result.push(
				...(criterion.criteria as Api.IContactFilterCriteria[]).map(child => ({
					...child,
					op: child.op ?? (Api.CONTACT_FILTER_OPERATOR.Or as Api.ContactFilterOperator),
				}))
			);
		} else {
			result.push(criterion);
		}
	}
	return result;
}

export function extractTagNamesFromContactFilter(filter?: Api.IContactFilterCriteria | null): string[] {
	if (!filter) {
		return [];
	}
	const names: string[] = [];
	const walk = (criterion: Api.IContactFilterCriteria) => {
		if (isTagCriterion(criterion) && criterion.value) {
			names.push(criterion.value);
		}
		criterion.criteria?.forEach(walk);
	};
	walk(filter);
	return Array.from(new Set(names));
}

export function buildTagContactFilter(tagNames: string[]): Api.IContactFilterCriteria | null {
	if (!tagNames.length) {
		return null;
	}
	return {
		criteria: tagNames.map(value => ({ property: Api.ContactFilterCriteriaProperty.Tag, value })),
		op: Api.FilterOperator.Or,
	};
}
